import { useCallback, useEffect, useState } from "react";
import {
  createAulaAdminCohort,
  createAulaAdminOrganization,
  deleteAulaAdminOrganization,
  getAulaAdminCohort,
  getAulaAdminCohorts,
  getAulaAdminOrganizations,
  updateAulaAdminOrganization,
} from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Copy, GraduationCap, ImageIcon, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

const REGISTRATION_BASE = "https://app.aurorageriatrica.com/register/aula?token=";

const emptyOrgForm = {
  name: "",
  country: "",
  contact_email: "",
  logo_url: "",
  active: true,
};

const emptyCohortForm = {
  organization_id: "",
  name: "",
  instructor_name: "",
  instructor_email: "",
  cohort_number: "",
  max_students: 20,
  active: true,
};

function coerceArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function ActiveBadge({ active }) {
  return active ? (
    <Badge className="border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Activa</Badge>
  ) : (
    <Badge className="border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-100">Inactiva</Badge>
  );
}

function OrgLogoCell({ logoUrl }) {
  const [failed, setFailed] = useState(false);
  const url = (logoUrl || "").trim();

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (!url || failed) {
    return (
      <div
        className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-slate-400"
        title="Sin logo"
      >
        <ImageIcon className="h-4 w-4" aria-hidden />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      className="h-8 w-8 rounded-md border border-slate-200 object-cover"
      width={32}
      height={32}
      onError={() => setFailed(true)}
    />
  );
}

export default function AdminAulaUniversidades() {
  const [organizations, setOrganizations] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingCohorts, setLoadingCohorts] = useState(true);

  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [orgEditingId, setOrgEditingId] = useState(null);
  const [orgForm, setOrgForm] = useState(emptyOrgForm);
  const [orgSaving, setOrgSaving] = useState(false);
  const [deleteOrgDialogOpen, setDeleteOrgDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(false);

  const [cohortDialogOpen, setCohortDialogOpen] = useState(false);
  const [cohortForm, setCohortForm] = useState(emptyCohortForm);
  const [cohortSaving, setCohortSaving] = useState(false);
  const [createdRegistrationLink, setCreatedRegistrationLink] = useState(null);

  const fetchOrganizations = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const res = await getAulaAdminOrganizations();
      setOrganizations(coerceArray(res?.data));
    } catch {
      setOrganizations([]);
      toast.error("Error al cargar universidades");
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  const fetchCohorts = useCallback(async () => {
    setLoadingCohorts(true);
    try {
      const res = await getAulaAdminCohorts();
      const list = coerceArray(res?.data);
      const withCounts = await Promise.all(
        list.map(async (cohort) => {
          const id = cohort?.id;
          if (!id) return { ...cohort, student_count: 0 };
          try {
            const detail = await getAulaAdminCohort(id);
            const students = detail?.data?.students;
            return { ...cohort, student_count: Array.isArray(students) ? students.length : 0 };
          } catch {
            return { ...cohort, student_count: 0 };
          }
        })
      );
      setCohorts(withCounts);
    } catch {
      setCohorts([]);
      toast.error("Error al cargar cohortes");
    } finally {
      setLoadingCohorts(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchCohorts();
  }, [fetchOrganizations, fetchCohorts]);

  const openCreateOrg = () => {
    setOrgForm(emptyOrgForm);
    setOrgEditingId(null);
    setOrgDialogOpen(true);
  };

  const openEditOrg = (org) => {
    setOrgForm({
      name: org.name || "",
      country: org.country || "",
      contact_email: org.contact_email || "",
      logo_url: org.logo_url || "",
      active: org.active !== false,
    });
    setOrgEditingId(org.id);
    setOrgDialogOpen(true);
  };

  const saveOrganization = async () => {
    if (!orgForm.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setOrgSaving(true);
    try {
      const payload = {
        name: orgForm.name.trim(),
        country: orgForm.country.trim(),
        contact_email: orgForm.contact_email.trim(),
        logo_url: orgForm.logo_url.trim(),
        active: orgForm.active,
      };
      if (orgEditingId) {
        await updateAulaAdminOrganization(orgEditingId, payload);
        toast.success("Universidad actualizada");
      } else {
        await createAulaAdminOrganization(payload);
        toast.success("Universidad creada");
      }
      setOrgDialogOpen(false);
      fetchOrganizations();
    } catch {
      toast.error(orgEditingId ? "Error al actualizar universidad" : "Error al crear universidad");
    } finally {
      setOrgSaving(false);
    }
  };

  const openDeleteOrgConfirm = (org) => {
    setOrgToDelete(org);
    setDeleteOrgDialogOpen(true);
  };

  const handleDeleteOrganization = async () => {
    if (!orgToDelete?.id) return;
    setDeletingOrg(true);
    try {
      await deleteAulaAdminOrganization(orgToDelete.id);
      toast.success("Universidad eliminada");
      setDeleteOrgDialogOpen(false);
      setOrgToDelete(null);
      fetchOrganizations();
      fetchCohorts();
    } catch {
      toast.error("Error al eliminar universidad");
    } finally {
      setDeletingOrg(false);
    }
  };

  const openCreateCohort = () => {
    setCohortForm({
      ...emptyCohortForm,
      organization_id: organizations[0]?.id || "",
    });
    setCreatedRegistrationLink(null);
    setCohortDialogOpen(true);
  };

  const saveCohort = async () => {
    if (!cohortForm.organization_id) {
      toast.error("Selecciona una universidad");
      return;
    }
    if (!cohortForm.name.trim()) {
      toast.error("El nombre de la cohorte es obligatorio");
      return;
    }
    setCohortSaving(true);
    try {
      const res = await createAulaAdminCohort({
        organization_id: cohortForm.organization_id,
        name: cohortForm.name.trim(),
        instructor_name: cohortForm.instructor_name.trim(),
        instructor_email: cohortForm.instructor_email.trim(),
        cohort_number: cohortForm.cohort_number.trim(),
        max_students: Number(cohortForm.max_students) || 20,
        active: cohortForm.active,
      });
      const token = res?.data?.registration_token;
      if (token) {
        setCreatedRegistrationLink(`${REGISTRATION_BASE}${token}`);
      }
      toast.success("Cohorte creada");
      fetchCohorts();
    } catch {
      toast.error("Error al crear cohorte");
    } finally {
      setCohortSaving(false);
    }
  };

  const copyRegistrationLink = async () => {
    if (!createdRegistrationLink) return;
    try {
      await navigator.clipboard.writeText(createdRegistrationLink);
      toast.success("Link copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar el link");
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sky-700">
            <GraduationCap className="h-6 w-6" />
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Aurora Aula — Universidades</h1>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            Gestiona universidades socias y cohortes de estudiantes
          </p>
        </div>
      </header>

      {/* Sección A — Universidades */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg">Universidades</CardTitle>
          <Button onClick={openCreateOrg} className="bg-sky-600 hover:bg-sky-700">
            <Plus className="mr-2 h-4 w-4" />
            Nueva universidad
          </Button>
        </CardHeader>
        <CardContent>
          {loadingOrgs ? (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Cargando universidades...
            </div>
          ) : organizations.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No hay universidades registradas.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[52px]">Logo</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>País</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[180px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <OrgLogoCell logoUrl={org.logo_url} />
                    </TableCell>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell>{org.country || "—"}</TableCell>
                    <TableCell>{org.contact_email || "—"}</TableCell>
                    <TableCell>
                      <ActiveBadge active={org.active !== false} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditOrg(org)}>
                          <Pencil className="mr-1 h-4 w-4" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteOrgConfirm(org)}
                        >
                          <Trash2 className="mr-1 h-4 w-4" />
                          Eliminar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Sección B — Cohortes */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg">Cohortes</CardTitle>
          <Button
            onClick={openCreateCohort}
            className="bg-sky-600 hover:bg-sky-700"
            disabled={organizations.length === 0}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nueva cohorte
          </Button>
        </CardHeader>
        <CardContent>
          {organizations.length === 0 && !loadingOrgs && (
            <p className="mb-4 text-sm text-amber-700">Crea al menos una universidad antes de abrir una cohorte.</p>
          )}
          {loadingCohorts ? (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Cargando cohortes...
            </div>
          ) : cohorts.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No hay cohortes registradas.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Universidad</TableHead>
                  <TableHead>Cohorte</TableHead>
                  <TableHead>Catedrático</TableHead>
                  <TableHead>Nº cohorte</TableHead>
                  <TableHead>Estudiantes</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cohorts.map((cohort) => (
                  <TableRow key={cohort.id}>
                    <TableCell>{cohort.organization_name || "—"}</TableCell>
                    <TableCell className="font-medium">{cohort.name}</TableCell>
                    <TableCell>{cohort.instructor_name || "—"}</TableCell>
                    <TableCell>{cohort.cohort_number || "—"}</TableCell>
                    <TableCell>{cohort.student_count ?? 0}</TableCell>
                    <TableCell>
                      <ActiveBadge active={cohort.active !== false} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog universidad */}
      <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{orgEditingId ? "Editar universidad" : "Nueva universidad"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="org-name">Nombre</Label>
              <Input
                id="org-name"
                value={orgForm.name}
                onChange={(e) => setOrgForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Universidad San Pablo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-country">País</Label>
              <Input
                id="org-country"
                value={orgForm.country}
                onChange={(e) => setOrgForm((f) => ({ ...f, country: e.target.value }))}
                placeholder="Guatemala"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-email">Email de contacto</Label>
              <Input
                id="org-email"
                type="email"
                value={orgForm.contact_email}
                onChange={(e) => setOrgForm((f) => ({ ...f, contact_email: e.target.value }))}
                placeholder="contacto@universidad.edu"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-logo">URL del logo</Label>
              <Input
                id="org-logo"
                value={orgForm.logo_url}
                onChange={(e) => setOrgForm((f) => ({ ...f, logo_url: e.target.value }))}
                placeholder="https://universidad.edu/logo.png"
              />
              <p className="text-xs text-slate-500">
                Pega la URL directa de la imagen del logo (PNG o JPG recomendado)
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <Label htmlFor="org-active">Universidad activa</Label>
              <Switch
                id="org-active"
                checked={orgForm.active}
                onCheckedChange={(checked) => setOrgForm((f) => ({ ...f, active: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrgDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveOrganization} disabled={orgSaving} className="bg-sky-600 hover:bg-sky-700">
              {orgSaving ? "Guardando..." : orgEditingId ? "Guardar cambios" : "Crear universidad"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog cohorte */}
      <Dialog
        open={cohortDialogOpen}
        onOpenChange={(open) => {
          setCohortDialogOpen(open);
          if (!open) setCreatedRegistrationLink(null);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{createdRegistrationLink ? "Cohorte creada" : "Nueva cohorte"}</DialogTitle>
          </DialogHeader>

          {createdRegistrationLink ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600">
                Comparte este link con los estudiantes para que se registren en Aurora Aula:
              </p>
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                <p className="break-all font-mono text-xs text-sky-900">{createdRegistrationLink}</p>
              </div>
              <Button type="button" onClick={copyRegistrationLink} className="w-full bg-sky-600 hover:bg-sky-700">
                <Copy className="mr-2 h-4 w-4" />
                Copiar link de registro
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Universidad</Label>
                <Select
                  value={cohortForm.organization_id}
                  onValueChange={(value) => setCohortForm((f) => ({ ...f, organization_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona universidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cohort-name">Nombre de la cohorte</Label>
                <Input
                  id="cohort-name"
                  value={cohortForm.name}
                  onChange={(e) => setCohortForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Residentes Geriatría 2026"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cohort-instructor">Nombre del catedrático</Label>
                <Input
                  id="cohort-instructor"
                  value={cohortForm.instructor_name}
                  onChange={(e) => setCohortForm((f) => ({ ...f, instructor_name: e.target.value }))}
                  placeholder="Dr. Joel Alejandro López Navarro"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cohort-instructor-email">Email del catedrático</Label>
                <Input
                  id="cohort-instructor-email"
                  type="email"
                  value={cohortForm.instructor_email}
                  onChange={(e) => setCohortForm((f) => ({ ...f, instructor_email: e.target.value }))}
                  placeholder="clinicadr.joellopez@gmail.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cohort-number">Número de cohorte</Label>
                  <Input
                    id="cohort-number"
                    value={cohortForm.cohort_number}
                    onChange={(e) => setCohortForm((f) => ({ ...f, cohort_number: e.target.value }))}
                    placeholder="2026-01"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cohort-max">Máximo de estudiantes</Label>
                  <Input
                    id="cohort-max"
                    type="number"
                    min={1}
                    value={cohortForm.max_students}
                    onChange={(e) => setCohortForm((f) => ({ ...f, max_students: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {createdRegistrationLink ? (
              <Button onClick={() => setCohortDialogOpen(false)}>Cerrar</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCohortDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={saveCohort} disabled={cohortSaving} className="bg-sky-600 hover:bg-sky-700">
                  {cohortSaving ? "Creando..." : "Crear cohorte"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOrgDialogOpen} onOpenChange={setDeleteOrgDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar universidad</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            ¿Eliminar{" "}
            <span className="font-semibold text-slate-900">{orgToDelete?.name}</span>? Se eliminarán
            también todas sus cohortes.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteOrgDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteOrganization}
              disabled={deletingOrg}
            >
              {deletingOrg ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
